export const EA_TABLE_MAX_ROWS = 10_000;

export interface ParsedTableFieldAddress {
    column: string;
    firstDataRow: number;
}

export interface ParseTableFieldAddressResult {
    success: true;
    value: ParsedTableFieldAddress;
}

export interface ParseTableFieldAddressError {
    success: false;
    error: string;
}

export type TableFieldAddressParseResult = ParseTableFieldAddressResult | ParseTableFieldAddressError;

const COLUMN_START_PATTERN = /^([A-Z]{1,3})\s*(\d+)$/i;
const COLUMN_RANGE_PATTERN = /^([A-Z]{1,3})\s*(\d+)\s*:\s*([A-Z]{1,3})\s*(\d+)$/i;

function isValidColumn(column: string): boolean {
    return /^[A-Z]{1,3}$/.test(column);
}

/**
 * Option B: canonical storage is column + start row with fixed maxRows from the dictionary.
 * Accepts full-range input (e.g. B8:B10007) and normalises to column + firstDataRow.
 */
export function parseTableFieldAddress(input: string): TableFieldAddressParseResult {
    const trimmed = input.trim();
    if (!trimmed) {
        return { success: false, error: "Address is required." };
    }

    const rangeMatch = COLUMN_RANGE_PATTERN.exec(trimmed);
    if (rangeMatch) {
        const startColumn = rangeMatch[1].toUpperCase();
        const startRow = Number(rangeMatch[2]);
        const endColumn = rangeMatch[3].toUpperCase();
        const endRow = Number(rangeMatch[4]);

        if (!isValidColumn(startColumn) || !isValidColumn(endColumn)) {
            return { success: false, error: "Invalid column letters in range." };
        }
        if (startColumn !== endColumn) {
            return { success: false, error: "Table address must use a single column range." };
        }
        if (!Number.isInteger(startRow) || startRow <= 0 || !Number.isInteger(endRow) || endRow <= 0) {
            return { success: false, error: "Row numbers must be positive integers." };
        }
        if (endRow < startRow) {
            return { success: false, error: "Range end row must be on or after the start row." };
        }

        return {
            success: true,
            value: { column: startColumn, firstDataRow: startRow },
        };
    }

    const cellMatch = COLUMN_START_PATTERN.exec(trimmed);
    if (cellMatch) {
        const column = cellMatch[1].toUpperCase();
        const firstDataRow = Number(cellMatch[2]);
        if (!isValidColumn(column)) {
            return { success: false, error: "Invalid column letters." };
        }
        if (!Number.isInteger(firstDataRow) || firstDataRow <= 0) {
            return { success: false, error: "Start row must be a positive integer." };
        }
        return {
            success: true,
            value: { column, firstDataRow },
        };
    }

    return { success: false, error: "Enter a column and start row (e.g. B8) or a single-column range (e.g. B8:B10007)." };
}

export function formatTableFieldAddress(column: string, firstDataRow: number): string {
    return `${column.toUpperCase()}${firstDataRow}`;
}
